import React from 'react';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';


export const commonGridSx = {
  border: 'none',
  width: '100%',
  maxWidth: '100%',
  fontFamily: "'Fira Sans', sans-serif",
  '& .MuiDataGrid-main, & .MuiDataGrid-virtualScroller, & .MuiDataGrid-columnHeaders': {
    width: '100%',
  },
  '& .MuiDataGrid-columnHeaders': {
    background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '15px',
    letterSpacing: '1px',
    color: 'rgb(103, 107, 132)',
    fontFamily: 'myFirstFont, "Fira Sans", sans-serif',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row': { transition: 'background 0.15s ease' },
  '& .MuiDataGrid-row:hover': { backgroundColor: '#f8fafc' },
  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#ebf4fb' },
  '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: '#d6eaf8' },
  '& .MuiDataGrid-cell': {
    fontSize: '1rem',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  '& .MuiDataGrid-footerContainer': { borderTop: '1px solid #f1f5f9', minHeight: 52, fontSize: '1rem' },
  '& .MuiCheckbox-root': { color: '#cbd5e1' },
  '& .MuiCheckbox-root.Mui-checked': { color: '#03407b' },
  '& .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: '#03407b' },
  '& .MuiDataGrid-sortIcon': { color: '#03407b' },
  '& .MuiDataGrid-menuIconButton': { color: '#94a3b8' },
  '& .MuiDataGrid-columnSeparator': { color: '#e2e8f0' },
  '& .MuiTablePagination-root': { fontSize: '1rem', color: '#64748b' },
  '& .MuiDataGrid-selectedRowCount': { fontSize: '1rem', color: '#03407b' },
  '& .MuiDataGrid-overlay': { background: '#fff' },

  /* ── Sticky action column (always visible, no Pro licence needed) ── */
  '& .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-cell[data-field="actions_col"], & .MuiDataGrid-cell[data-field="action"]': {
    position: 'sticky',
    right: 0,
    backgroundColor: '#ffffff',
    zIndex: 3,
    boxShadow: '-3px 0 8px rgba(0,0,0,0.06)',
    borderLeft: '1px solid #f1f5f9',
    minWidth: '130px !important',
  },
  '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="actions_col"], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="action"]': {
    backgroundColor: '#f8fafc',
  },
  '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field="actions_col"], & .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell[data-field="action"]': {
    backgroundColor: '#ebf4fb',
  },
  '& .MuiDataGrid-columnHeader[data-field="actions"], & .MuiDataGrid-columnHeader[data-field="actions_col"], & .MuiDataGrid-columnHeader[data-field="action"]': {
    position: 'sticky',
    right: 0,
    backgroundColor: '#f8fafc',
    zIndex: 4,
    boxShadow: '-3px 0 8px rgba(0,0,0,0.05)',
    borderLeft: '1px solid #e2e8f0',
    minWidth: '130px !important',
  },
  '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader[data-field="actions_col"] .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader[data-field="action"] .MuiDataGrid-columnHeaderTitle': {
    overflow: 'visible',
    whiteSpace: 'nowrap',
  },
};


export const gridContainerSx = {
  width: '100%',
  maxWidth: '100%',
  borderRadius: '16px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
  overflow: 'hidden',
  background: '#fff',
  height: 'calc(100vh - 220px)',
  minHeight: 400,
};


export const gridLocaleText = {
  noRowsLabel: 'Aucun enregistrement trouvé.',
  footerRowSelected: (count) => `${count} élément(s) sélectionné(s)`,
  MuiTablePagination: {
    labelRowsPerPage: 'Lignes par page :',
    labelDisplayedRows: ({ from, to, count }) =>
      `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`,
  },
};


export const quickFilterSx = {
  '& .MuiInputBase-root': {
    borderRadius: '10px',
    fontSize: '1rem',
    background: '#f8fafc',
    px: 1.5,
    height: 36,
    fontFamily: "'Fira Sans', sans-serif",
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#7fb3d3' },
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#03407b' },
};


export const toolbarButtonSx = {
  textTransform: 'none',
  fontSize: '1rem',
  color: '#64748b',
  borderRadius: '8px',
  fontFamily: "'Fira Sans', sans-serif",
  '&:hover': { background: '#f1f5f9', color: '#334155' },
};

/**
 * Returns a zero-config toolbar component.
 *
 * @param {string}       title     — section title shown in the toolbar
 * @param {React.FC}     ExtraSlot — optional component rendered between title and search
 */
export function makeGridToolbar(title, ExtraSlot) {
  return function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #f1f5f9',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 700,
            color: '#0f172a',
            mr: 'auto',
            fontFamily: "'Fira Sans', sans-serif",
          }}
        >
          {title}
        </Typography>

        {ExtraSlot && <ExtraSlot />}

        <GridToolbarQuickFilter
          sx={quickFilterSx}
          placeholder="Rechercher…"
          debounceMs={600}
        />
        <GridToolbarColumnsButton sx={toolbarButtonSx} />
        <GridToolbarFilterButton sx={toolbarButtonSx} />
        <GridToolbarDensitySelector sx={toolbarButtonSx} />
      </GridToolbarContainer>
    );
  };
}

/**
 * Toolbar with optional left (e.g. action buttons) and right (search/filter) zones.
 *
 * @param {object} options
 *   title       — label shown left
 *   LeftSlot    — optional component rendered after the title
 *   hideSearch  — suppress quick-filter
 *   hideColumns — suppress columns button
 *   hideDensity — suppress density button
 */
export function makePageToolbar({ title, LeftSlot, hideSearch, hideColumns, hideDensity } = {}) {
  return function PageToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: '1px solid #f1f5f9',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          minHeight: 56,
        }}
      >
        {title && (
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#0f172a',
              fontFamily: "'Fira Sans', sans-serif",
            }}
          >
            {title}
          </Typography>
        )}

        {LeftSlot && <LeftSlot />}

        <Box sx={{ flex: 1 }} />

        {!hideSearch && (
          <GridToolbarQuickFilter sx={quickFilterSx} placeholder="Rechercher…" debounceMs={600} />
        )}
        {!hideColumns && <GridToolbarColumnsButton sx={toolbarButtonSx} />}
        <GridToolbarFilterButton sx={toolbarButtonSx} />
        {!hideDensity && <GridToolbarDensitySelector sx={toolbarButtonSx} />}
      </GridToolbarContainer>
    );
  };
}
